<script>
	import { tick } from "svelte";
	import { simpleDateTime, watchObject } from "cando-util";

	import { store } from "$lib/utilities/store";
	import { webSocketMessages } from "$lib/stores/webSocketMessages.js";

	export let docId;
	// block/detail rendering stretches to the available height; in a table
	// cell we cap the height instead so rows stay a sane size
	export let fill = false;

	let scrollContainer;
	// once the user scrolls away from the bottom we stop yanking them back down
	let stickToBottom = true;

	$: call = $store.calls?.docs?.[docId] || {};

	$: houseParties = [
		...new Set(
			(call.ringCentralEvents || []).flatMap((e) =>
				(e.notification?.body?.parties || [])
					.filter((p) => !!p.extensionId)
					.map((p) => p.id),
			),
		),
	];

	$: messages = [
		...(call.callMonitorTranscriptEvents || []),
		...($webSocketMessages?.messages || [])
			.filter(
				(m) =>
					m?.data?.sessionId ===
						call?.ringCentral?.telephonySessionId && m?.data?.text,
			)
			.map((m) => m?.data),
	]
		.map((m) => ({
			speaker: !!houseParties.includes(m.partyId) ? "Agent" : "Caller",
			content: m.text,
			timestamp: m.date || m.timestamp || m.occurredAt,
			houseParty: !!houseParties.includes(m.partyId),
		}))
		.reduce((a, v) => ({ ...a, [v.timestamp]: { ...v } }), {});

	// oldest first so the newest message lands at the bottom, chat style
	$: messageIds = Object.keys(messages).sort();

	const scrollToLatest = async (behavior = "smooth") => {
		await tick();
		if (!scrollContainer) return;
		scrollContainer.scrollTo({
			top: scrollContainer.scrollHeight,
			behavior,
		});
	};

	const onScroll = () => {
		if (!scrollContainer) return;
		const distanceFromBottom =
			scrollContainer.scrollHeight -
			scrollContainer.scrollTop -
			scrollContainer.clientHeight;
		stickToBottom = distanceFromBottom < 40;
	};

	// jump to the latest on first paint, then follow new messages as they land
	let renderedCount = -1;
	$: if (scrollContainer && messageIds.length !== renderedCount) {
		const firstPaint = renderedCount === -1;
		renderedCount = messageIds.length;
		if (stickToBottom) scrollToLatest(firstPaint ? "auto" : "smooth");
	}
</script>

<!-- fill: the scroll box is taken out of flow (absolute) so its content cannot
     grow the grid row - the row is sized by its neighbours, floored at min-h-96 -->
<div class={`w-full ${fill ? "relative h-full min-h-96" : ""}`}>
	{#if messageIds.length}
		<div
			bind:this={scrollContainer}
			on:scroll={onScroll}
			class={`pr-2 space-y-2 overflow-y-auto whitespace-normal ${
				fill ? "absolute inset-0" : "max-h-96"
			}`}
		>
			{#each messageIds as messageId (messageId)}
				<div
					class={`chat ${messages[messageId].houseParty ? "chat-end" : "chat-start"} my-2`}
				>
					<div class="flex gap-2 chat-header">
						<div>
							{messages[messageId].speaker}
						</div>
						<div>
							<time class="text-xs opacity-50"
								>{simpleDateTime(
									messages[messageId].timestamp,
								)}</time
							>
						</div>
					</div>

					<div
						class={`chat-bubble ${messages[messageId].houseParty ? "chat-bubble-primary" : ""}`}
					>
						{messages[messageId].content}
					</div>
				</div>
			{/each}
		</div>
	{:else}
		<p class="text-sm text-base-content/60">
			No transcript segments available.
		</p>
	{/if}
</div>
